import React, { useCallback } from 'react'
import { View, Text, Pressable, Platform, ActivityIndicator } from 'react-native'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../shared/constants/animations'
import { useAccountAdminCopy } from '../../hooks/useAccountAdminCopy'
import type { MemberActionPending } from '../../hooks/useAccountAdminDialogs'
import type { B2bMemberRow } from '../../types/b2bAccount.types'
import type { Theme } from '../../../theme/getTheme'
import { ACCOUNT_MEMBERS_TAB_TEST_IDS } from '../../constants/accountAdmin.constants'
import type { getAccountMembersTabStyles } from './AccountMembersTab.styles'
import type { B2bMemberTableNameEmail } from '../../helpers/b2bAccount.helper'

export interface AccountMemberTableRowProps {
  member: B2bMemberRow
  currentUserId: string
  adminCount: number
  index: number
  nameEmail: B2bMemberTableNameEmail
  joinedLabel: string
  roleLabel: string
  isAdminRole: boolean
  isLastRow: boolean
  styles: ReturnType<typeof getAccountMembersTabStyles>
  theme: Theme
  isNarrow: boolean
  selectionMode: boolean
  selected: boolean
  onToggleSelect: (userId: string) => void
  onPromoteToAdmin: (userId: string) => Promise<void>
  onDemoteFromAdmin: (userId: string) => Promise<void>
  onRemoveMember: (userId: string) => Promise<void>
  memberActionPending: MemberActionPending | null
  membersDirectoryBusy: boolean
}

export const AccountMemberTableRow: React.FC<AccountMemberTableRowProps> = ({
  member: m,
  currentUserId,
  adminCount,
  index,
  nameEmail,
  joinedLabel,
  roleLabel,
  isAdminRole,
  isLastRow,
  styles,
  theme,
  isNarrow,
  selectionMode,
  selected,
  onToggleSelect,
  onPromoteToAdmin,
  onDemoteFromAdmin,
  onRemoveMember,
  memberActionPending,
  membersDirectoryBusy,
}) => {
  const copy = useAccountAdminCopy()
  const handlePromote = useCallback(() => void onPromoteToAdmin(m.userId), [m.userId, onPromoteToAdmin])
  const handleDemote = useCallback(() => void onDemoteFromAdmin(m.userId), [m.userId, onDemoteFromAdmin])
  const handleRemove = useCallback(() => void onRemoveMember(m.userId), [m.userId, onRemoveMember])

  const isThisRowPending = memberActionPending?.userId === m.userId
  const actionsDisabled = membersDirectoryBusy

  const showPromote =
    !selectionMode && m.userId !== currentUserId && m.role === 'MEMBER'
  const showDemote =
    !selectionMode && m.userId !== currentUserId && m.role === 'ADMIN' && adminCount > 1
  const showRemove = !selectionMode && m.userId !== currentUserId
  const showSelect = selectionMode && m.userId !== currentUserId
  const handleToggleSelect = useCallback(() => onToggleSelect(m.userId), [m.userId, onToggleSelect])

  const indexEl = !selectionMode ? (
    <View
      style={styles.tableColIndex}
      testID={ACCOUNT_MEMBERS_TAB_TEST_IDS.rowIndex(index)}
    >
      <Text style={styles.tableIndexText}>{index}</Text>
    </View>
  ) : null

  const checkboxEl = selectionMode ? (
    <View style={styles.tableColCheckbox}>
      {showSelect ? (
        <Pressable
          accessibilityRole="checkbox"
          accessibilityState={{ checked: selected }}
          accessibilityLabel={copy.MEMBERS_BULK_ROW_CHECKBOX_A11Y}
          disabled={actionsDisabled}
          onPress={handleToggleSelect}
          style={({ pressed, hovered }) => [
            styles.checkboxHitArea,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && !actionsDisabled && styles.secondaryButtonPressed,
            hovered && Platform.OS === 'web' && !actionsDisabled && styles.secondaryButtonHover,
            actionsDisabled && styles.bulkToolbarBtnDisabled,
          ]}
        >
          <View style={[styles.checkboxOuter, selected && styles.checkboxOuterSelected]}>
            {selected ? <View style={styles.checkboxInnerDot} /> : null}
          </View>
        </Pressable>
      ) : null}
    </View>
  ) : null

  const nameEl = (
    <View style={styles.tableColName}>
      <Text style={styles.tableMemberPrimary} numberOfLines={1} ellipsizeMode="tail">
        <Text style={styles.tableMemberPrimaryName}>{nameEmail.nameColumn}</Text>
        {m.userId === currentUserId ? (
          <Text style={styles.tableMemberPrimaryYou}>{copy.MEMBER_ROW_YOU_INLINE}</Text>
        ) : null}
      </Text>
    </View>
  )

  const emailEl = (
    <View style={styles.tableColEmail}>
      <Text style={styles.tableEmailText} numberOfLines={1} ellipsizeMode="tail">
        {nameEmail.emailColumn}
      </Text>
    </View>
  )

  const roleEl = (
    <View style={styles.tableColRole}>
      <View style={[styles.roleBadge, isAdminRole ? styles.roleBadgeAdmin : styles.roleBadgeMember]}>
        <Text style={isAdminRole ? styles.roleBadgeAdminText : styles.roleBadgeMemberText}>
          {roleLabel}
        </Text>
      </View>
    </View>
  )

  const joinedEl = (
    <View style={styles.tableColJoined}>
      <Text style={styles.tableJoinedText}>{joinedLabel}</Text>
    </View>
  )

  const actionsEl = isThisRowPending ? (
    <View
      style={[styles.tableColActions, styles.tableColActionsPending]}
      accessibilityRole="progressbar"
      accessibilityLabel={copy.MEMBER_ACTION_LOADING_A11Y}
    >
      <ActivityIndicator size="small" color={theme.primary} />
    </View>
  ) : (
    <View style={styles.tableColActions}>
      {showPromote ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={copy.MAKE_ADMIN}
          disabled={actionsDisabled}
          onPress={handlePromote}
          style={({ pressed, hovered }) => [
            styles.makeAdminBtn,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && !actionsDisabled && styles.makeAdminBtnPressed,
            hovered && Platform.OS === 'web' && !actionsDisabled && styles.makeAdminBtnHover,
            actionsDisabled && styles.makeAdminBtnDisabled,
          ]}
        >
          <Text style={styles.makeAdminBtnText}>{copy.MAKE_ADMIN}</Text>
        </Pressable>
      ) : null}
      {showDemote ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={copy.DEMOTE_ADMIN}
          disabled={actionsDisabled}
          onPress={handleDemote}
          style={({ pressed, hovered }) => [
            styles.demoteBtn,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && !actionsDisabled && styles.demoteBtnPressed,
            hovered && Platform.OS === 'web' && !actionsDisabled && styles.demoteBtnHover,
            actionsDisabled && styles.demoteBtnDisabled,
          ]}
        >
          <Text style={styles.demoteBtnText}>{copy.DEMOTE_ADMIN}</Text>
        </Pressable>
      ) : null}
      {showRemove ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={copy.REMOVE_MEMBER}
          disabled={actionsDisabled}
          onPress={handleRemove}
          style={({ pressed }) => [
            styles.removeBtn,
            pressed && !actionsDisabled && styles.removeBtnPressed,
            actionsDisabled && styles.removeBtnDisabled,
          ]}
        >
          <Text style={styles.removeBtnText}>{copy.REMOVE_MEMBER}</Text>
        </Pressable>
      ) : null}
      {!showPromote && !showDemote && !showRemove ? (
        <Text
          style={styles.tableActionsPlaceholder}
          accessibilityLabel={copy.MEMBERS_TABLE_NO_ACTIONS_A11Y}
        >
          {copy.MEMBERS_TABLE_NO_ACTIONS_VISIBLE}
        </Text>
      ) : null}
    </View>
  )

  if (isNarrow) {
    return (
      <View style={[styles.tableDataRow, isLastRow && styles.tableDataRowLast]}>
        <View style={styles.narrowLine1}>
          {checkboxEl}
          {indexEl}
          {nameEl}
          {roleEl}
        </View>
        <View style={styles.narrowLineEmail}>
          {emailEl}
        </View>
        <View style={styles.mobileDivider} />
        <View style={styles.narrowLine2}>
          {joinedEl}
          {actionsEl}
        </View>
      </View>
    )
  }

  return (
    <View style={[styles.tableDataRow, isLastRow && styles.tableDataRowLast]}>
      {checkboxEl}
      {indexEl}
      {nameEl}
      {emailEl}
      {roleEl}
      {joinedEl}
      {actionsEl}
    </View>
  )
}
